import { FunctionComponent, useEffect, useState } from 'react';
import { Button, SelectMultiple, Table, TableRow, TableCell, Autocomplete, TextField } from '../../tailwindcomponents';
import { Typography, Input } from '../../tailwindcomponents';
import { DatePicker } from '../../MaterialUiComponents';
import taskApi from '../../api/tasks';
import lineApi from '../../api/line';
import { HandoverReportSearchObj } from '../../types/line';

type LinePlannerIssueHistoryProps = {
    setLoading: (loading: boolean) => void;
}

const departmentTypes = [
    { id: 0, name: "Line Planning" },
    { id: 1, name: "Line" },
    { id: 2, name: "Lite" },
    { id: 3, name: "Maintenance Control" },
];

const DEFAULT_DEPARTMENTS = [0, 1, 2, 3];
const DAYS_LOOKBACK = 90;

const getDefaultFromDate = (): Date => {
    const d = new Date();
    d.setDate(d.getDate() - DAYS_LOOKBACK);
    return d;
};

const LinePlannerIssueHistory: FunctionComponent<LinePlannerIssueHistoryProps> = ({ setLoading }) => {
    const [allTails, setAllTails] = useState<string[]>([]);
    const [selectedTailNumber, setSelectedTailNumber] = useState<string>("");
    const [searchText, setSearchText] = useState("");
    const [selectedDepartments, setSelectedDepartments] = useState<number[]>(DEFAULT_DEPARTMENTS);
    const [fromDate, setFromDate] = useState<Date | null>(getDefaultFromDate());
    const [toDate, setToDate] = useState<Date | null>(new Date());
    const [allIssues, setAllIssues] = useState<HandoverReportSearchObj[]>([]);
    const [filteredIssues, setFilteredIssues] = useState<HandoverReportSearchObj[]>([]);

    useEffect(() => {
        setLoading(true);
        taskApi.GetAllTailNumbers()
            .then(res => {
                setAllTails(res.data);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        refresh();
    }, [selectedDepartments, selectedTailNumber]);

    useEffect(() => {
        applyFilters(searchText, allIssues, fromDate, toDate);
    }, [searchText, fromDate, toDate]);

    const applyFilters = (
        txt: string,
        source: Array<HandoverReportSearchObj>,
        from: Date | null,
        to: Date | null,
    ) => {
        let out = source;
        if (from) {
            const fromTs = new Date(from);
            fromTs.setHours(0, 0, 0, 0);
            out = out.filter(r => new Date(r.created_date).getTime() >= fromTs.getTime());
        }
        if (to) {
            const toTs = new Date(to);
            toTs.setHours(23, 59, 59, 999);
            out = out.filter(r => new Date(r.created_date).getTime() <= toTs.getTime());
        }
        if (txt.length > 0) {
            out = out.filter(s => s.issue.toUpperCase().includes(txt.toUpperCase()));
        }
        setFilteredIssues(out);
    };

    const refresh = () => {
        const tempDepartments: string[] = selectedDepartments.map(row => departmentTypes[row].name);
        setLoading(true);
        lineApi.GetHandoverReportsByTail(selectedTailNumber, tempDepartments)
            .then(res => {
                if (res.status) {
                    setAllIssues(res.data);
                    applyFilters(searchText, res.data, fromDate, toDate);
                }
            })
            .finally(() => setLoading(false));
    };

    const generateTableBody = (row: HandoverReportSearchObj, index: number) => (
        <TableRow key={`${row.tail_number}-${row.created_date}-${index}`}>
            <TableCell>{index}</TableCell>
            <TableCell>{row.created_date_text}</TableCell>
            <TableCell>{row.tail_number}</TableCell>
            <TableCell>{row.issue}</TableCell>
            <TableCell>{row.created_by_name}</TableCell>
            <TableCell>{row.department}</TableCell>
        </TableRow>
    );

    const onClearFilters = () => {
        setSelectedDepartments(DEFAULT_DEPARTMENTS);
        setSelectedTailNumber("");
        setSearchText("");
        setFromDate(getDefaultFromDate());
        setToDate(new Date());
    };

    return (
        <div>
            <div className="flex flex-wrap items-end gap-2 mb-3">
                <div className="w-full sm:w-[112px]">
                    <Autocomplete
                        options={allTails}
                        value={selectedTailNumber || null}
                        freeSolo
                        onChange={(_e: any, newValue: string | null) => {
                            setSelectedTailNumber(newValue ?? "");
                        }}
                        renderInput={(params: any) => (
                            <TextField {...params} label="Tail number" variant="standard" />
                        )}
                    />
                </div>
                <div className="w-full sm:w-[132px]">
                    <DatePicker
                        type="Date"
                        selected={fromDate}
                        label="Created date from"
                        onDateChange={(date: Date | null) => setFromDate(date)}
                    />
                </div>
                <div className="w-full sm:w-[132px]">
                    <DatePicker
                        type="Date"
                        selected={toDate}
                        label="Created date to"
                        onDateChange={(date: Date | null) => setToDate(date)}
                    />
                </div>
                <div className="w-full sm:w-[260px]">
                    <Input
                        multiline={false}
                        label_text="Search by text"
                        help_text=""
                        type="text"
                        value={searchText}
                        width={100}
                        onTextChange={(value: string) => setSearchText(value)}
                    />
                </div>
                <div className="w-full sm:w-[176px]">
                    <SelectMultiple
                        optionsObject={departmentTypes}
                        onSelectedItem={(e: any) => setSelectedDepartments(e.target.value)}
                        selectedItems={selectedDepartments}
                        width={100}
                        widthPercentage
                        filter_by_text="Filter by Departments"
                    />
                </div>
                <Button variant="text" color="primary" onClick={onClearFilters}>Clear filters</Button>
            </div>
            <div className="flex flex-wrap justify-center">
                <Typography variant="h4" text={"issues(" + filteredIssues.length + ")"} align="center" margin={[1, 0, 1, 0]} />
            </div>
            <div>
                <Table
                    headers={["Created Date", "Tail Number", "Issue", "Created By", "Department"]}
                    rows={filteredIssues}
                    generateBody={(row: any, index: number) => generateTableBody(row, index)}
                    pagination={true}
                    defaultRowsPerPage={100}
                    skipIndexCol={false}
                />
            </div>
        </div>
    );
};

export default LinePlannerIssueHistory;
